import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import hubServiceApi from 'shared/api/hub.service.api';
import { EVENTS } from 'shared/constants';
import analyticsService from 'src/helpers/analytics.service';

const ContentPreviewContext = createContext({
  isHub: false,
  isPublic: false,
  itemId: null,
  setItemId: () => {},
  item: {},
  error: null,
  loading: false,
  analyticsTriggers: {},
});

export function useContentPreview() {
  return useContext(ContentPreviewContext);
}
let sourceOrganizationName = null;
let sourceOrganizationDepartment = null;

const ANALYTICS_TRIGGERS = {
  onImport: payload => {
    analyticsService.track(EVENTS.KNOWLEDGE_HUB.IMPORT, payload);
    sourceOrganizationName = payload.sourceOrganizationName;
    sourceOrganizationDepartment = payload.sourceOrganizationDepartment;
  },
  onImportDismiss: payload => analyticsService.track(EVENTS.KNOWLEDGE_HUB.IMPORT_DISMISS, payload),
  onImportEditContent: payload =>
    analyticsService.track(EVENTS.KNOWLEDGE_HUB.IMPORT_EDIT_CONTENT, payload),
  onPublish: payload =>
    analyticsService.track(EVENTS.KNOWLEDGE_HUB.PUBLISH, {
      sourceOrganizationName,
      sourceOrganizationDepartment,
      ...payload,
    }),
  onShare: payload => analyticsService.track(EVENTS.KNOWLEDGE_HUB.SHARE, payload),
};

/**
 * Content Preview Provider
 * @export
 * @param {{
 * children: React.ReactNode;
 * isHub?: boolean;
 * analyticsTriggers?: {
 *  onImport: (payload) => void;
 *  onImportDismiss: (payload) => void;
 *  onImportEditContent: (payload) => void;
 *  onPublish: (payload) => void;
 *  onShare: (payload) => void;
 * }
 * }} { children, isHub, analyticsTriggers }
 * @returns
 */
export function ContentPreviewProvider({
  children,
  isHub = false,
  analyticsTriggers = ANALYTICS_TRIGGERS,
}) {
  const [itemId, setItemId] = useState(null);
  const [item, setItem] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getItem = useCallback(async id => {
    setError(null);
    setLoading(true);

    const { data, error } = await hubServiceApi.getItem({ id });

    if (error || !data) {
      setError(error);
      setLoading(false);
      return;
    }

    setItem(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isHub || !itemId) {
      return;
    }

    getItem(itemId);
  }, [itemId, getItem, isHub]);

  const isPublic = item?.organization?.isPublicContentProvider ?? false;
  const value = { isHub, isPublic, itemId, setItemId, item, error, loading, analyticsTriggers };

  return <ContentPreviewContext.Provider value={value}>{children}</ContentPreviewContext.Provider>;
}
